import React from "react";
import StatusBadge from "./StatusBadge";
import { Progress } from "@/components/ui/progress";
import { useSettings } from "@/context/SettingsContext";
import { formatOdds, formatCurrency } from "@/lib/format";
import { BellOff, Bell } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

function BetLegRow({ leg, idx, oddsFmt }) {
  const isProp = leg.market_type === "player_prop";
  const isTotal = leg.market_type === "total";
  const isTeamFG = leg.market_type === "team_field_goals" && leg.team_side !== "both" && leg.over_under !== "each";
  const val = leg.live_stat_value ?? 0;
  const line = leg.line ?? 0;
  const pct = line ? Math.min(100, Math.max(0, (val / line) * 100)) : 0;
  const showBar = (isProp || isTotal || isTeamFG) && line > 0;
  return (
    <div className="p-3 rounded-lg bg-[#0E1420] border border-white/5" data-testid={`leg-row-${idx}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          {leg.away_logo && <img src={leg.away_logo} alt="" className="h-5 w-5" />}
          <span className="text-xs font-mono text-slate-300">{leg.away_team} @ {leg.home_team}</span>
          {leg.home_logo && <img src={leg.home_logo} alt="" className="h-5 w-5" />}
        </div>
        <StatusBadge status={leg.status} testid={`leg-status-badge-${idx}`} />
      </div>
      <div className="flex items-baseline justify-between mb-1">
        <div className="text-sm font-semibold text-white">{leg.pick}</div>
        <div className="font-mono text-sm text-emerald-300">{formatOdds(leg.odds, oddsFmt)}</div>
      </div>
      {leg.game_detail && (
        <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">
          {leg.game_detail}{typeof leg.home_score === "number" ? ` · ${leg.away_team} ${leg.away_score} - ${leg.home_score} ${leg.home_team}` : ""}
        </div>
      )}
      {showBar ? (
        <div>
          <div className="flex items-center justify-between text-xs mb-1">
            <span className="font-mono text-slate-400">{val} / {line}</span>
            <span className="text-slate-500">{pct.toFixed(0)}%</span>
          </div>
          <Progress value={pct} className="h-1.5 bg-white/10" />
        </div>
      ) : leg.live_note ? (
        <div className="text-xs text-slate-400 font-mono">{leg.live_note}</div>
      ) : null}
    </div>
  );
}

export default function BetSlipCard({ slip, onOpen, onMuteChange }) {
  const { settings } = useSettings();
  const bookmaker = slip.bookmaker || "Custom";
  const muted = !!slip.notifications_muted;

  const toggleMute = async (e) => {
    e.stopPropagation();
    try {
      await api.post(`/slips/${slip.slip_id}/mute`, { muted: !muted });
      toast.success(!muted ? "Slip muted" : "Slip unmuted");
      onMuteChange?.(slip.slip_id, !muted);
    } catch { toast.error("Failed"); }
  };

  return (
    <div
      data-testid={`bet-slip-card-${slip.slip_id}`}
      onClick={onOpen}
      className="cursor-pointer rounded-xl bg-[#121824] border border-white/10 hover:border-emerald-500/40 shadow-xl transition-colors p-4 sm:p-5"
    >
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500">{bookmaker}</div>
          <div className="text-lg font-bold uppercase tracking-wide text-white" style={{ fontFamily: "Barlow Condensed, sans-serif" }}>
            {slip.slip_type} · {slip.legs.length} Leg{slip.legs.length > 1 ? "s" : ""}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid={`mute-toggle-${slip.slip_id}`}
            onClick={toggleMute}
            title={muted ? "Unmute notifications" : "Mute notifications"}
            className={`h-8 w-8 rounded-md flex items-center justify-center border transition-colors ${muted ? "border-red-500/30 bg-red-500/10 text-red-300" : "border-white/10 bg-white/5 text-slate-300 hover:text-white"}`}
          >
            {muted ? <BellOff className="h-4 w-4" /> : <Bell className="h-4 w-4" />}
          </button>
          <StatusBadge status={slip.status} size="lg" testid={`slip-status-${slip.slip_id}`} />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2 mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Odds</div>
          <div className="font-mono font-bold text-white">{formatOdds(slip.total_odds, settings.odds_format)}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Stake</div>
          <div className="font-mono font-bold text-white">{formatCurrency(slip.stake, settings.currency)}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Returns</div>
          <div className="font-mono font-bold text-emerald-300">{formatCurrency(slip.projected_payout, settings.currency)}</div>
        </div>
      </div>
      <div className="space-y-2">
        {slip.legs.map((leg, i) => <BetLegRow key={leg.leg_id || i} leg={leg} idx={i} oddsFmt={settings.odds_format} />)}
      </div>
    </div>
  );
}
